import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, X, MessageCircle, Send, Check } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useMessaging } from './MessagingContext';
import { useUserData } from './UserDataContext';
import { Yap } from '../types';

interface ShareYapModalProps {
  isOpen: boolean;
  onClose: () => void;
  yap: Yap;
}

export function ShareYapModal({ isOpen, onClose, yap }: ShareYapModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });
  const { addMessage } = useMessaging();
  const { friends, userProfile } = useUserData();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedFriends([]);
      setMessage('');
      setIsSending(false);
    }
  }, [isOpen]);

  // Filter friends based on search query
  const filteredFriends = friends.filter(friend => 
    friend.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    friend.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFriendSelection = (friendId: string) => {
    setSelectedFriends(prev => 
      prev.includes(friendId)
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId]
    );
  };

  const handleSend = async () => {
    if (selectedFriends.length === 0) return;

    setIsSending(true);
    setSendProgress({ current: 0, total: selectedFriends.length });
    let successCount = 0;

    try {
      // 🔒 PATCH: Use sequential loop instead of Promise.all to respect 1s rate limit
      // and prevent overwhelmeing the Firestore connection.
      for (const friendId of selectedFriends) {
        const friend = friends.find(f => String(f.id) === friendId || (f as any).uid === friendId);
        if (!friend) continue;

        const canonicalId = (friend as any)?.uid || String(friend.id);

        setSendProgress(prev => ({ ...prev, current: prev.current + 1 }));

        // Create the shared yap object structure - 🔒 SANITIZED: Nullify undefined fields for Firestore
        const sharedYapData = {
          id: yap.id,
          author: yap.author || 'User',
          username: yap.username || '',
          avatar: yap.avatar || null,
          content: yap.content || '',
          time: yap.time || 'Recently',
          likes: yap.likes || 0,
          comments: yap.comments || 0,
          shouts: yap.shouts || 0,
          image: yap.image || null,
          friendsOnly: yap.friendsOnly || false,
          originalYap: yap.originalYap ? {
             id: yap.originalYap.id || '',
             author: yap.originalYap.author || '',
             username: yap.originalYap.username || '',
             content: yap.originalYap.content || '',
             avatar: yap.originalYap.avatar || null,
             time: yap.originalYap.time || 'Recently'
          } : null
        };

        const newMessage = {
            id: `${Date.now()}-${Math.random()}`,
            sender: 'user' as const,
            text: message || `Shared a ${yap.shoutCaption ? 'shout' : 'yap'} from ${yap.author}`,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: Date.now(),
            sharedYap: sharedYapData
        };

        try {
            console.log(`[ShareYapModal] Attempting share with friend: ${canonicalId} (OriginalSelection: ${friendId})`);
            await addMessage(canonicalId, newMessage);
            successCount++;
            
            // 🔒 RATE LIMIT: Firestore rules enforce 1s between messages per user
            if (selectedFriends.length > 1) {
                await new Promise(r => setTimeout(r, 1100));
            }
        } catch (msgErr: any) {
            console.error(`[ShareYapModal] Failed to send to ${canonicalId}:`, msgErr.message || msgErr);
            // We continue to the next friend, unless it's a global failure
        }
      }

      // Increment shares counter on the yap (non-blocking)
      try {
        const { YapService } = await import('../services/api');
        await (YapService as any).shareYap(yap.id);
      } catch (shareErr) {
        console.warn('Failed to increment share count:', shareErr);
      }

      if (successCount > 0) {
        toast.success(`Shared with ${successCount} friend${successCount !== 1 ? 's' : ''}`);
        onClose();
      } else {
        toast.error('Failed to share with any selected friends.');
      }
    } catch (error: any) {
      console.error('Error sharing yap:', error);
      toast.error(error.message || 'Failed to share yap. Please try again.');
    } finally {
      setIsSending(false);
      setSendProgress({ current: 0, total: 0 });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 text-black dark:text-white p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between bg-[#f2deb3] dark:bg-[#121212]">
          <h2 className="text-lg font-bold">Share Yap</h2>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-black/10 dark:border-white/10">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                    placeholder="Search friends..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-black/5 dark:bg-white/5 border-transparent focus:border-[#F39C12] rounded-xl"
                />
            </div>
        </div>

        {/* Friends List */}
        <ScrollArea className="h-64 p-2">
            {friends.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-4 text-gray-500">
                    <p>You haven't added any friends yet.</p>
                    <Button 
                        variant="link" 
                        onClick={() => {
                            onClose();
                            window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'discover' }));
                        }}
                        className="text-[#F39C12]"
                    >
                        Find people to follow
                    </Button>
                </div>
            ) : filteredFriends.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                    No friends found matching "{searchQuery}"
                </div>
            ) : (
                <div className="space-y-1">
                    {filteredFriends.map((friend) => (
                        <div
                            key={friend.id}
                            onClick={() => toggleFriendSelection(String(friend.id))}
                            className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                                selectedFriends.includes(String(friend.id))
                                    ? 'bg-[#F39C12]/10 dark:bg-[#F39C12]/20'
                                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10 border border-black/10 dark:border-white/10">
                                    <AvatarImage src={friend.image || friend.avatar} />
                                    <AvatarFallback>{friend.name[0]}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <p className="font-medium text-sm text-black dark:text-white">{friend.name}</p>
                                    <p className="text-xs text-gray-500">@{friend.username?.replace('@', '')}</p>
                                </div>
                            </div>
                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                                selectedFriends.includes(String(friend.id))
                                    ? 'bg-[#F39C12] border-[#F39C12] text-white'
                                    : 'border-gray-400 dark:border-gray-600'
                            }`}>
                                {selectedFriends.includes(String(friend.id)) && <Check className="w-3 h-3" />}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </ScrollArea>

        {/* Message Input & Send Button */}
        <div className="p-4 border-t border-black/10 dark:border-white/10 bg-[#f9f9f9] dark:bg-[#121212]">
            <div className="flex flex-col gap-3">
                <Input
                    placeholder="Write a message (optional)..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 focus:border-[#F39C12] rounded-xl"
                />
                <Button 
                    onClick={handleSend}
                    disabled={selectedFriends.length === 0 || isSending}
                    className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#4a90e2] hover:from-[#E67E22] hover:to-[#C0392B] dark:hover:from-[#3b6cd4] dark:hover:to-[#357abd] text-white rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSending ? (
                        <span className="flex items-center gap-2">
                             Sharing ({sendProgress.current}/{sendProgress.total})...
                        </span>
                    ) : (
                        <span className="flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            Send to {selectedFriends.length > 0 ? `${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}` : 'Friends'}
                        </span>
                    )}
                </Button>
            </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
